import type { ReactNode } from "react";
import type { DomainLoanApplicationDetails } from "@porchpass/api-client";
import {
  StatusCircleEmpty,
  StatusCircleHalfIcon,
  StatusCircleThreeQuarterIcon,
  StatusCircleSevenEighthsIcon,
  StatusCircleCompleted,
  CancelCircle,
} from "src/assets/icons";
import {
  APPLICATION_STATUSES,
  APPLICATION_STATUS_KEYS,
  ApplicationStatusList,
} from "src/constants";
import {
  CLEAR_TO_CLOSE_TEXT,
  CLOSED_TEXT,
  DECLINED_TEXT,
  FUNDED_TEXT,
  PRE_APPROVED_TEXT,
} from "src/constants/colors";
import { ApplicationStatusBadge } from "src/components/ApplicationStatusBadge";
import type { AppMilestoneType } from "src/components/ApplicationStatusBadge";
import Dropdown from "src/components/Dropdown";

interface StatusDisplayProps {
  applicationStatus: string;
  isPorchpassLender: boolean;
  updateApplicationDetails: (
    details: Partial<DomainLoanApplicationDetails>,
  ) => Promise<void>;
  refetchApplication: () => Promise<void>;
}

export const StatusDisplay = ({
  applicationStatus,
  isPorchpassLender,
  updateApplicationDetails,
  refetchApplication,
}: StatusDisplayProps) => {
  const onApplicationStatusChange = (status: string) => {
    if (
      APPLICATION_STATUS_KEYS[status as keyof typeof APPLICATION_STATUS_KEYS]
    ) {
      void updateApplicationDetails({
        application_status:
          APPLICATION_STATUS_KEYS[
            status as keyof typeof APPLICATION_STATUS_KEYS
          ],
      }).then(() => {
        void refetchApplication();
      });
    }
  };

  const getStatusForDropdown = (status: string) => {
    switch (status) {
      case APPLICATION_STATUSES.SUBMITTED:
      case APPLICATION_STATUSES.INVITED:
      case APPLICATION_STATUSES.READY_TO_SUBMIT:
        return <ApplicationStatusBadge status={status} />;
      case APPLICATION_STATUSES.PRE_APPROVED:
        return (
          <StatusCircleHalfIcon
            fill={PRE_APPROVED_TEXT}
            width={18}
            height={18}
          />
        );
      case APPLICATION_STATUSES.CLEAR_TO_CLOSE:
        return (
          <StatusCircleThreeQuarterIcon
            fill={CLEAR_TO_CLOSE_TEXT}
            width={18}
            height={18}
          />
        );
      case APPLICATION_STATUSES.CLOSED:
        return (
          <StatusCircleSevenEighthsIcon
            fill={CLOSED_TEXT}
            width={18}
            height={18}
          />
        );
      case APPLICATION_STATUSES.FUNDED:
        return (
          <StatusCircleCompleted fill={FUNDED_TEXT} width={18} height={18} />
        );
      case APPLICATION_STATUSES.DECLINED:
        return <CancelCircle fillColor={DECLINED_TEXT} opacity="100" />;
      default:
        return <StatusCircleEmpty width={18} height={18} />;
    }
  };

  const applicationStatusOptions = ApplicationStatusList.filter(
    (status) =>
      status !== APPLICATION_STATUSES.SUBMITTED &&
      status !== APPLICATION_STATUSES.INVITED &&
      status !== APPLICATION_STATUSES.READY_TO_SUBMIT,
  ).reduce<Record<string, { label: string; icon: ReactNode }>>(
    (acc, status) => ({
      ...acc,
      [status]: {
        label: status,
        icon: getStatusForDropdown(status),
      },
    }),
    {},
  );

  if (
    applicationStatus === APPLICATION_STATUS_KEYS.Invited ||
    applicationStatus === APPLICATION_STATUS_KEYS["Ready to submit"]
  ) {
    return getStatusForDropdown(
      APPLICATION_STATUSES[
        applicationStatus as keyof typeof APPLICATION_STATUSES
      ],
    );
  }

  if (isPorchpassLender) {
    return (
      <ApplicationStatusBadge
        status={
          APPLICATION_STATUSES[
            applicationStatus as keyof typeof APPLICATION_STATUSES
          ] as AppMilestoneType
        }
      />
    );
  }

  return (
    <Dropdown
      title="Manually set status"
      menuItemsObj={applicationStatusOptions}
      selectedItem={applicationStatus ? applicationStatus : ""}
      onSelectedItemChange={onApplicationStatusChange}
      hideCheckmark
      customTrigger={(isOpen) => (
        <ApplicationStatusBadge
          status={
            APPLICATION_STATUSES[
              applicationStatus as keyof typeof APPLICATION_STATUSES
            ] as AppMilestoneType
          }
          isDropdown={true}
          isOpen={isOpen}
        />
      )}
    />
  );
};
